import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import * as ROUTES from '@constants/routes';

import {
  useCreateMedicalCondition,
  useUpdateMedicalCondition,
} from '@hooks/api/medical-condition';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout, DrilldownItem } from '@layouts/DrilldownStyle';

import { getMedicalConditionTemplate } from '@models/fhir-templates/medical-condition';
import type {
  ConditionValues,
  FormValues,
  MedicalConditionRecord,
} from '@models/medical-condition';
import type { DrilldownProps } from '@models/record-drilldown';

import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import { ConditionClinicalStatusCode } from 'generated/types';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  diagnosed: {
    id: 'medicalConditionsDrilldown.diagnosed',
    defaultMessage: 'Diagnosed',
  },
  title: {
    id: 'medicalConditionsDrilldown.title',
    defaultMessage: 'Medical Condition',
  },
  snomedCode: {
    id: 'medicalConditionsDrilldown.snomedCode',
    defaultMessage: 'Snomed CT Code',
  },
  icdCode: {
    id: 'medicalConditionsDrilldown.icdCode',
    defaultMessage: 'ICD 10 CM Code',
  },
  vitallCode: {
    id: 'medicalConditionsDrilldown.vitallCode',
    defaultMessage: 'Notes',
  },
  clinicalStatus: {
    id: 'vitalSigns.clinicalStatus',
    defaultMessage: 'Clinical Status',
  },
  category: {
    id: 'medicalConditionsDrilldown.category',
    defaultMessage: 'Category',
  },
  onsetDate: {
    id: 'medicalConditionsDrilldown.onsetDate',
    defaultMessage: 'Onset Date',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  condition: {
    id: 'medicalConditionsDrilldown.condition',
    defaultMessage: 'Condition',
  },
});

export const CONDITION_STATUS_OPTIONS = [
  { k: ConditionClinicalStatusCode.active, v: 'Active' },
  { k: ConditionClinicalStatusCode.inactive, v: 'Inactive' },
];

interface MedicalConditionProps extends DrilldownProps<MedicalConditionRecord> {
  /**
   * When true, suppresses the default redirect to the MEDICAL_CONDITIONS route
   * after successfully creating a new medical condition.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const MedicalCondition: React.FC<MedicalConditionProps> = ({
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  patientId: patient_id,
  isCurator,
  isCreating,
  isEditing,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [createCondition, createConditionLoading] = useCreateMedicalCondition();
  const [updateCondition, updateConditionLoading] = useUpdateMedicalCondition();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isModifying = isCreating || isEditing;

  const loading =
    baseLoading ||
    createConditionLoading ||
    updateConditionLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.condition ?? '',
    category: data?.category ?? '',
    clinicalStatus: data?.clinicalStatus ?? '',
    icd10Code: data?.icd10Code ?? '',
    onsetDateTime: data?.onsetDateTime ?? '',
    snomedCode: data?.snomedCode ?? '',
    vitallCode: data?.vitallCode ?? '',
    selfReported: data?.selfReported ?? false,
    associatedResources: [],
  };

  const conditionValidationSchema: Schema<Partial<ConditionValues>> =
    object().shape({
      title: string().required(formatMessage(messages.required)),
      category: string(),
      clinicalStatus: string(),
      icd10Code: string(),
      onsetDateTime: string(),
      snomedCode: string(),
      vitallCode: string(),
      selfReported: boolean().required(formatMessage(messages.required)),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
    });

  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = conditionValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) {
      return;
    }
    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let medCondition: MedicalConditionRecord | null | undefined;

    try {
      if (data?.id) {
        const { data: updatedCond } = await updateCondition({
          input: {
            id: data.id,
            condition: data.getModelInput(values),
          },
        });
        medCondition = updatedCond;
      } else {
        const { data } = await createCondition({
          input: {
            ...getMedicalConditionTemplate(patient_id, values),
          },
        });
        medCondition = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: medCondition?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (medCondition && isCreating && !disableNavigateOnCreate) {
        navigate(`${ROUTES.MEDICAL_CONDITIONS}${medCondition.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.clinicalStatus)}
                name="clinicalStatus"
                onChange={(value) => setFieldValue('clinicalStatus', value)}
                options={CONDITION_STATUS_OPTIONS}
                type="dropdown"
                value={data?.clinicalStatus}
              />
              <DrilldownItem
                label={formatMessage(messages.category)}
                name="category"
                value={data?.category}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.snomedCode)}
                name="snomedCode"
                value={data?.snomedCode}
              />
              <DrilldownItem
                label={formatMessage(messages.icdCode)}
                name="icd10Code"
                value={data?.icd10Code}
              />
            </ColumnLayout>

            <DrilldownItem
              label={formatMessage(messages.vitallCode)}
              name="vitallCode"
              value={data?.vitallCode}
            />

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps = {
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <FormikFhirDateField
              formats={['date', 'year']}
              label={formatMessage(messages.onsetDate)}
              name="onsetDateTime"
              onChange={(value) => {
                setFieldValue('onsetDateTime', value);
              }}
              value={data?.onsetDateTime}
              max={true}
            />
          ) : (
            data?.getConditionOnset()
          ),
          title: data?.condition || formatMessage(messages.title),
          titleInput: (
            <FormikTextField
              name="title"
              placeholder={formatMessage(messages.condition)}
            />
          ),
        };
        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={ROUTES.MEDICAL_CONDITIONS}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default MedicalCondition;
